import type { ReactNode } from 'react';
import { cn } from './cn';
import { statusTone, humanizeStatus, type StatusTone } from '../lib/statusTone';

const TONE: Record<StatusTone, { badge: string; dot: string }> = {
  neutral: { badge: 'bg-surface-3 text-muted border-line', dot: 'bg-subtle' },
  info: { badge: 'bg-sky-500/12 text-sky-600 border-sky-500/25 dark:text-sky-400', dot: 'bg-sky-500' },
  processing: { badge: 'bg-violet-500/12 text-violet-600 border-violet-500/25 dark:text-violet-400', dot: 'bg-violet-500' },
  success: { badge: 'bg-brand-500/12 text-brand-700 border-brand-500/25 dark:text-brand-300', dot: 'bg-brand-500' },
  warning: { badge: 'bg-warning-500/15 text-warning-600 border-warning-500/30 dark:text-harvest-300', dot: 'bg-warning-500' },
  danger: { badge: 'bg-danger-500/12 text-danger-600 border-danger-500/30 dark:text-danger-500', dot: 'bg-danger-500' },
  accent: { badge: 'bg-harvest-400/18 text-harvest-700 border-harvest-400/35 dark:text-harvest-300', dot: 'bg-harvest-400' },
};

export interface StatusBadgeProps {
  /** Status code — colour is derived from `lib/statusTone` unless `tone` overrides it. */
  status?: string | null;
  /** Force a tone regardless of the status code. */
  tone?: StatusTone;
  /** Override the displayed text (defaults to a humanized status). */
  label?: ReactNode;
  /** Show the leading status dot (default true). */
  dot?: boolean;
  /** Pulse the dot — for live/processing states. Respects reduced motion (via the global CSS guard). */
  pulse?: boolean;
  className?: string;
}

/**
 * One consistent status pill for the whole app. Colour comes from the shared
 * status→tone map so a given status reads the same everywhere (light + dark).
 */
export function StatusBadge({ status, tone, label, dot = true, pulse, className }: StatusBadgeProps) {
  const t = tone ?? statusTone(status);
  const c = TONE[t];
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-pill border px-2 py-px type-label leading-5',
        c.badge,
        className,
      )}
    >
      {dot && <span className={cn('size-1.5 shrink-0 rounded-full', c.dot, pulse && 'animate-pulse')} />}
      {label ?? humanizeStatus(status)}
    </span>
  );
}
